import React, { useEffect, useState } from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import {
  ConversationStartersWidgetProps,
  ConversationStartersWidgetState,
  ConversationStartersPreset,
} from "./types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../../../utils/fontWeightOptions";
import {
  CONVERSATION_STARTERS_DEFAULT_STATE,
  CONVERSATION_STARTERS_PRESETS,
} from "./presets";

/**
 * Load a Google Font stylesheet into document.head.
 * This is necessary because fonts loaded inside Shadow DOM don't register
 * @font-face declarations globally, causing the font to not render.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  // Check if already loaded
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;

  // Inject into document.head (works with Shadow DOM)
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

// Skeleton component for loading state
const SkeletonPill: React.FC<{ width: number }> = ({ width }) => (
  <div
    style={{
      width: `${width}px`,
      height: "32px",
      borderRadius: "9999px",
      backgroundColor: "#e5e7eb",
      animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
    }}
  />
);

const PRESET_OPTIONS = [
  { label: "Inline Pills (V1)", value: "inline" },
  { label: "Highlighted (V2)", value: "highlighted" },
  { label: "Accordion (V3)", value: "accordion" },
  { label: "Accordion Highlighted (V4)", value: "accordion-highlighted" },
  { label: "Outline (Pages)", value: "pages" },
  { label: "Custom", value: "custom" },
];

const editFields: EditField[] = [
  // Preset selector (top of panel)
  {
    key: "preset",
    label: "Preset",
    type: "options",
    options: PRESET_OPTIONS,
  },

  // Title section
  { key: "showTitle", label: "Show Title", type: "toggle" },
  { key: "showSparkleIcon", label: "Show Sparkle Icon", type: "toggle" },
  { key: "collapsible", label: "Collapsible (Accordion)", type: "toggle" },
  { key: "titleText", label: "Title Text", type: "text" },
  {
    key: "titleFontSize",
    label: "Title Font Size",
    type: "number",
    min: 14,
    max: 24,
    step: 1,
  },
  {
    key: "titleFontWeight",
    label: "Title Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  { key: "titleFontFamily", label: "Title Font Family", type: "font" },
  { key: "titleColor", label: "Title Color", type: "color" },

  // Container
  {
    key: "containerGap",
    label: "Gap Between Title and Pills",
    type: "number",
    min: 8,
    max: 32,
    step: 4,
  },
  {
    key: "containerMarginTop",
    label: "Container Margin Top",
    type: "number",
    min: 0,
    max: 64,
    step: 4,
  },
  {
    key: "containerMarginBottom",
    label: "Container Margin Bottom",
    type: "number",
    min: 0,
    max: 64,
    step: 4,
  },
  { key: "containerBgColor", label: "Container Background", type: "color" },
  {
    key: "containerPaddingX",
    label: "Container Padding X",
    type: "number",
    min: 0,
    max: 32,
    step: 4,
  },
  {
    key: "containerPaddingY",
    label: "Container Padding Y",
    type: "number",
    min: 0,
    max: 32,
    step: 4,
  },
  {
    key: "containerBorderRadius",
    label: "Container Border Radius",
    type: "number",
    min: 0,
    max: 24,
    step: 2,
  },

  // Pill styling
  {
    key: "pillPaddingX",
    label: "Pill Padding X",
    type: "number",
    min: 4,
    max: 24,
    step: 2,
  },
  {
    key: "pillPaddingY",
    label: "Pill Padding Y",
    type: "number",
    min: 2,
    max: 20,
    step: 2,
  },
  {
    key: "pillBorderRadius",
    label: "Pill Border Radius",
    type: "number",
    min: 0,
    max: 50,
    step: 2,
  },
  {
    key: "pillBorderWidth",
    label: "Pill Border Width",
    type: "number",
    min: 0,
    max: 3,
    step: 0.5,
  },
  { key: "pillBorderColor", label: "Pill Border Color", type: "color" },
  { key: "pillBgColor", label: "Pill Background", type: "color" },
  { key: "pillBgColorHover", label: "Pill Hover Background", type: "color" },
  { key: "pillTextColor", label: "Pill Text Color", type: "color" },
  {
    key: "pillFontSize",
    label: "Pill Font Size",
    type: "number",
    min: 12,
    max: 18,
    step: 1,
  },
  {
    key: "pillFontWeight",
    label: "Pill Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  { key: "pillFontFamily", label: "Pill Font Family", type: "font" },

  // "Ask anything" pill
  { key: "showAskAnything", label: 'Show "Ask anything" Pill', type: "toggle" },
  { key: "askAnythingLabel", label: '"Ask anything" Label', type: "text" },
];

// Map font weight keywords to CSS values
const fontWeightMap: Record<string, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

// Sparkle icon component for the title
const SparkleIcon: React.FC<{ color?: string }> = ({
  color = "currentColor",
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="19"
    height="19"
    viewBox="0 0 19 19"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.6375 8.97842L15.9115 8.19942C13.4152 7.49564 11.4643 5.54472 10.7605 3.04842L9.98148 0.32242C9.90136 0.12736 9.71136 0 9.50048 0C9.2896 0 9.09961 0.12736 9.01948 0.32242L8.24048 3.04842C7.5367 5.54472 5.58578 7.49564 3.08948 8.19942L0.36348 8.97842C0.14841 9.03946 0 9.23586 0 9.45942C0 9.68298 0.14841 9.87938 0.36348 9.94042L3.08948 10.7194C5.58578 11.4232 7.53671 13.3741 8.24048 15.8704L9.01948 18.5964C9.08052 18.8115 9.27692 18.9599 9.50048 18.9599C9.72404 18.9599 9.92044 18.8115 9.98148 18.5964L10.7605 15.8704C11.4642 13.3741 13.4152 11.4232 15.9115 10.7194L18.6375 9.94042C18.8526 9.87938 19.001 9.68298 19.001 9.45942C19.001 9.23586 18.8526 9.03946 18.6375 8.97842Z"
      fill={color}
    />
  </svg>
);

// Chevron icon for accordion toggle
const ChevronIcon: React.FC<{ expanded: boolean; color?: string }> = ({
  expanded,
  color = "currentColor",
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      flexShrink: 0,
      transition: "transform 0.2s ease",
      transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Individual pill button component
const QuestionPill: React.FC<{
  label: string;
  onClick: () => void;
  state: ConversationStartersWidgetState;
  resolveColor: (color: any) => string;
}> = ({ label, onClick, state, resolveColor }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: `${state.pillPaddingY}px ${state.pillPaddingX}px`,
        borderRadius: `${state.pillBorderRadius}px`,
        border: `${state.pillBorderWidth}px solid ${resolveColor(state.pillBorderColor)}`,
        backgroundColor: isHovered
          ? resolveColor(state.pillBgColorHover)
          : resolveColor(state.pillBgColor),
        color: resolveColor(state.pillTextColor),
        fontSize: `${state.pillFontSize}px`,
        fontWeight: fontWeightMap[state.pillFontWeight] || 500,
        fontFamily: state.pillFontFamilyCss || "inherit",
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        textAlign: "left",
        lineHeight: "32px",
      }}
    >
      {label}
    </button>
  );
};

export const ConversationStartersWidget: React.FC<
  ConversationStartersWidgetProps
> = ({
  widgetId = "conversation-starters",
  displayName = "Conversation Starters",
  initialState = {},
  questions,
  isLoading = false,
  onQuestionClick,
  onAskAnything,
}) => {
  const {
    state,
    isLoading: isThemeLoading,
    isSaving,
    handleEditChange: rawHandleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
  } = useWidgetEditable<ConversationStartersWidgetState>(
    widgetId,
    { ...CONVERSATION_STARTERS_DEFAULT_STATE, ...initialState },
    editFields
  );

  // Accordion expanded/collapsed state
  const [isExpanded, setIsExpanded] = useState(false);

  // Wrap handleEditChange to apply preset values when preset changes
  const handleEditChange = (key: string, value: any) => {
    if (key === "preset" && value !== "custom") {
      const presetKey = value as Exclude<ConversationStartersPreset, "custom">;
      const presetValues = CONVERSATION_STARTERS_PRESETS[presetKey];
      if (presetValues) {
        // Apply the preset key first
        rawHandleEditChange("preset", value);
        // Then apply all preset values
        Object.entries(presetValues).forEach(([k, v]) => {
          rawHandleEditChange(k, v);
        });
        return;
      }
    }
    rawHandleEditChange(key, value);
  };

  // Resolve pill font family
  const pillFontKey = state.pillFontFamily;
  const pillFontData =
    pillFontKey && pillFontKey !== "inherit" ? themeFonts[pillFontKey] : null;

  // Resolve title font family
  const titleFontKey = state.titleFontFamily;
  const titleFontData =
    titleFontKey && titleFontKey !== "inherit"
      ? themeFonts[titleFontKey]
      : null;

  // For Google Fonts, we need to load via <link> tag in document.head
  const pillGoogleFontLink =
    pillFontData && pillFontData.format === "google" ? pillFontData.url : null;
  const titleGoogleFontLink =
    titleFontData && titleFontData.format === "google"
      ? titleFontData.url
      : null;

  // Load Google Fonts into document.head when font changes
  useEffect(() => {
    if (pillGoogleFontLink) {
      loadGoogleFontInHead(pillGoogleFontLink);
    }
  }, [pillGoogleFontLink]);

  useEffect(() => {
    if (titleGoogleFontLink) {
      loadGoogleFontInHead(titleGoogleFontLink);
    }
  }, [titleGoogleFontLink]);

  // Don't render anything while theme is loading
  if (isThemeLoading) {
    return null;
  }

  const showAskAnything = state.showAskAnything && !!onAskAnything;

  // With no questions, the "Ask anything" pill alone keeps the widget on screen.
  if (!isLoading && questions.length === 0 && !showAskAnything) {
    return null;
  }

  // Determine font-family CSS for pills
  const pillFontFamilyCss = pillFontData
    ? pillFontData.format === "system"
      ? pillFontData.url
      : `"${pillFontData.name}", sans-serif`
    : "inherit";

  // Determine font-family CSS for title
  const titleFontFamilyCss = titleFontData
    ? titleFontData.format === "system"
      ? titleFontData.url
      : `"${titleFontData.name}", sans-serif`
    : "inherit";

  // Determine if we need @font-face for pills
  const needsPillFontFace =
    pillFontData &&
    pillFontData.format !== "system" &&
    pillFontData.format !== "google";

  // Determine if we need @font-face for title
  const needsTitleFontFace =
    titleFontData &&
    titleFontData.format !== "system" &&
    titleFontData.format !== "google";

  // Generate unique class for this widget instance
  const uniqueClass = `conversation-starters-${widgetId || "default"}`;

  // Generate @font-face CSS for uploaded fonts
  const styleCss = `
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    ${
      needsPillFontFace
        ? `
      @font-face {
        font-family: "${pillFontData.name}";
        src: url("${pillFontData.url}") format("${pillFontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
    ${
      needsTitleFontFace
        ? `
      @font-face {
        font-family: "${titleFontData.name}";
        src: url("${titleFontData.url}") format("${titleFontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
  `.trim();

  // Create state with computed font CSS for rendering
  const stateWithFonts: ConversationStartersWidgetState = {
    ...state,
    pillFontFamilyCss,
    titleFontFamilyCss,
  };

  // Determine if pills should be visible
  const showPills = state.collapsible ? isExpanded : true;

  // Resolve container background
  const containerBg = resolveColor(state.containerBgColor);
  const hasContainerBg =
    containerBg && containerBg !== "transparent" && containerBg !== "";

  const widgetContent = (
    <>
      {styleCss && <style>{styleCss}</style>}
      <div
        className={uniqueClass}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: showPills ? `${state.containerGap}px` : "0px",
          marginTop: `${state.containerMarginTop}px`,
          marginBottom: `${state.containerMarginBottom}px`,
          backgroundColor: hasContainerBg ? containerBg : undefined,
          padding:
            hasContainerBg || state.containerPaddingX || state.containerPaddingY
              ? `${state.containerPaddingY}px ${state.containerPaddingX}px`
              : undefined,
          borderRadius:
            state.containerBorderRadius > 0
              ? `${state.containerBorderRadius}px`
              : undefined,
        }}
      >
        {state.showTitle && (
          <h3
            onClick={
              state.collapsible
                ? () => setIsExpanded((prev) => !prev)
                : undefined
            }
            style={{
              fontSize: `${state.titleFontSize}px`,
              fontWeight: fontWeightMap[state.titleFontWeight] || 600,
              fontFamily: titleFontFamilyCss,
              color: resolveColor(state.titleColor),
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: state.collapsible ? "pointer" : undefined,
              userSelect: state.collapsible ? "none" : undefined,
            }}
          >
            {state.showSparkleIcon && (
              <SparkleIcon color={resolveColor(state.titleColor)} />
            )}
            <span style={{ flex: 1 }}>{state.titleText}</span>
            {state.collapsible && (
              <ChevronIcon
                expanded={isExpanded}
                color={resolveColor(state.titleColor)}
              />
            )}
          </h3>
        )}

        {showPills && (
          <>
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  alignItems: "flex-start",
                }}
              >
                <SkeletonPill width={180} />
                <SkeletonPill width={140} />
                <SkeletonPill width={200} />
                <SkeletonPill width={160} />
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  alignItems: "flex-start",
                  width: "100%",
                }}
              >
                {questions.map((question, index) => (
                  <QuestionPill
                    key={index}
                    label={question}
                    onClick={() => onQuestionClick(question)}
                    state={stateWithFonts}
                    resolveColor={resolveColor}
                  />
                ))}
                {showAskAnything && (
                  <QuestionPill
                    label={state.askAnythingLabel}
                    onClick={() => onAskAnything?.()}
                    state={stateWithFonts}
                    resolveColor={resolveColor}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
